'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { cn } from '../../lib/utils';
import { useIsElementScrolled } from '../../hooks/use-is-element-scrolled';
import { ModalControlContext, ModalControlContextType } from '../modal/modal-control-context';
import { IsScrolledContextProvider, useIsScrolledContext } from '../modal/is-scrolled-context';

export type SheetSide = 'left' | 'right';

export const SheetPortal = DialogPrimitive.Portal;

export const SheetOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="sheet-overlay"
    className={cn(
      'fixed inset-0 z-50 bg-navy-growth/70',
      'data-[state=open]:animate-in data-[state=open]:fade-in-0',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}>
    {children}
  </DialogPrimitive.Overlay>
));
SheetOverlay.displayName = 'SheetOverlay';

export type SheetProps = Omit<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
  'open' | 'defaultOpen'
> & {
  isOpen: boolean;
  side?: SheetSide;
  className?: string;
  /** Function to force close the sheet bypassing onOpenChange logic. */
  forceClose?: () => void;
  classNames?: {
    overlay?: string;
  };
} & Pick<ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'onOpenAutoFocus'>;

export const Sheet = forwardRef<ElementRef<typeof DialogPrimitive.Content>, SheetProps>(
  (
    {
      children,
      isOpen,
      onOpenChange,
      forceClose,
      side = 'left',
      className,
      classNames,
      onOpenAutoFocus,
      ...props
    },
    ref,
  ) => {
    const controlContextValue = useMemo(
      () =>
        ({
          isOpen,
          onOpenChange,
          close: () => onOpenChange?.(false),
          forceClose,
        }) satisfies ModalControlContextType,
      [isOpen, onOpenChange, forceClose],
    );

    return (
      <ModalControlContext.Provider value={controlContextValue}>
        <DialogPrimitive.Root open={isOpen} onOpenChange={onOpenChange} {...props}>
          <IsScrolledContextProvider>
            <SheetPortal>
              <SheetOverlay className={cn('z-[10000]', classNames?.overlay)} />
              <DialogPrimitive.Content
                ref={ref}
                aria-describedby={undefined} // Suppress Radix warning: no DialogDescription, intentionally omitted
                data-slot="sheet-panel"
                data-side={side}
                onOpenAutoFocus={onOpenAutoFocus}
                className={cn(
                  'fixed inset-y-0 z-[10000] flex h-full w-full max-w-[500px] flex-col bg-white shadow-bb-shadow',
                  'data-[side=left]:left-0 data-[side=right]:right-0',
                  'duration-200',
                  'data-[state=open]:animate-in data-[state=open]:duration-300 data-[state=open]:fade-in-0',
                  'data-[side=left]:data-[state=open]:slide-in-from-left',
                  'data-[side=right]:data-[state=open]:slide-in-from-right',
                  'data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=closed]:fade-out-0',
                  'data-[side=left]:data-[state=closed]:slide-out-to-left',
                  'data-[side=right]:data-[state=closed]:slide-out-to-right',
                  className,
                )}>
                {children}
              </DialogPrimitive.Content>
            </SheetPortal>
          </IsScrolledContextProvider>
        </DialogPrimitive.Root>
      </ModalControlContext.Provider>
    );
  },
);
Sheet.displayName = 'Sheet';

/**
 * Scrollable content area. Pin-to-bottom footer + sticky top header work because
 * this element is `flex-1 overflow-y-auto` inside the column-flex panel.
 */
export const SheetContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isScrolled } = useIsElementScrolled(ref);
  const { setIsContentScrolled } = useIsScrolledContext();

  useEffect(() => {
    setIsContentScrolled?.(isScrolled);
  }, [isScrolled, setIsContentScrolled]);

  return (
    <div
      data-slot="sheet-content"
      ref={ref}
      className={cn(
        'flex-1 overflow-y-auto px-6 py-3 text-left text-space scrollbar-thin',
        className,
      )}
      {...props}
    />
  );
};

export const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="sheet-footer"
    className={cn('flex justify-end gap-3 px-6 pt-3 pb-6', className)}
    {...props}
  />
);
